import React from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  IconButton,
  ButtonBase,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import ExitToApp from '@mui/icons-material/ExitToApp';
import LightMode from '@mui/icons-material/LightMode';
import DarkMode from '@mui/icons-material/DarkMode';
import MenuOpen from '@mui/icons-material/MenuOpen';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import ChevRightIcon from '@mui/icons-material/ChevronRight';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import TuneIcon from '@mui/icons-material/Tune';
import LockIcon from '@mui/icons-material/LockOutlined';
import AboutIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';
import type { User } from '../contexts/AuthContext';
import { userHasPassword } from '../contexts/userHasPassword';
import { shadow, FOCUS_RING } from '../theme/tokens';
import { PersonAvatar } from '../components/shared/PersonAvatar';
import { ProfileAvatarDialog } from '../components/shared/ProfileAvatarDialog';
import { NotificationBell } from '../components/notifications-inbox';
import { SECURITY_SECTION_ID } from '../components/settings/sections';
import { ABOUT_COPY } from '../components/about/copy';
import { BrandMark } from './BrandMark';
import { GlobalSearch } from './GlobalSearch';

interface TopBarProps {
  isDarkMode: boolean;
  toggleTheme: () => void;
  user: User | null;
  userMenuAnchor: HTMLElement | null;
  activeDrawerWidth: number;
  crumb?: string;
  onDrawerToggle: () => void;
  onUserMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onUserMenuClose: () => void;
  onLogout: () => void;
}

export function TopBar({
  isDarkMode,
  toggleTheme,
  user,
  userMenuAnchor,
  activeDrawerWidth,
  crumb,
  onDrawerToggle,
  onUserMenuOpen,
  onUserMenuClose,
  onLogout,
}: TopBarProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const appBarShadow = isDarkMode ? shadow.dark.bar : shadow.light.bar;
  const isExpanded = activeDrawerWidth >= 200;
  const [profileDialogOpen, setProfileDialogOpen] = React.useState(false);

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || '';

  const crumbParts = crumb ? crumb.split(' · ') : [];
  const focusRing = FOCUS_RING(alpha(theme.palette.primary.main, 0.35));

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: '100%',
        zIndex: theme.zIndex.drawer + 1,
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        boxShadow: appBarShadow,
        color: 'text.primary',
      }}
    >
      <Toolbar
        disableGutters
        sx={{ height: 56, minHeight: '56px !important', px: '20px', gap: 2 }}
      >
        {/* Mobile hamburger */}
        <IconButton
          aria-label="Open navigation"
          edge="start"
          onClick={onDrawerToggle}
          sx={{ mr: 1, display: { sm: 'none' }, color: 'text.secondary' }}
        >
          <MenuOpen />
        </IconButton>

        {/* Brand section — aligns with sidebar column width */}
        <Box
          sx={{
            flex: `0 0 ${activeDrawerWidth}px`,
            transition: 'flex-basis 0.2s ease-out',
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            gap: '10px',
            overflow: 'hidden',
          }}
        >
          <BrandMark size={34} />
          {isExpanded && (
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: 'text.primary',
                letterSpacing: '-0.01em',
                whiteSpace: 'nowrap',
              }}
            >
              Teamlane
            </Typography>
          )}
        </Box>

        {/* Breadcrumb */}
        {crumbParts.length > 0 && (
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: '4px',
              flexShrink: 0,
            }}
          >
            {crumbParts.map((part, i) => (
              <React.Fragment key={i}>
                <Typography
                  sx={{
                    fontSize: 12.5,
                    color: i === crumbParts.length - 1 ? 'text.primary' : 'text.disabled',
                    fontWeight: i === crumbParts.length - 1 ? 500 : 400,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {part}
                </Typography>
                {i < crumbParts.length - 1 && (
                  <ChevRightIcon sx={{ fontSize: 11, color: theme.palette.ink.faint }} />
                )}
              </React.Fragment>
            ))}
          </Box>
        )}

        <Box sx={{ flex: 1 }} />
        <GlobalSearch />
        <Box sx={{ flex: 1 }} />

        {/* Mode toggle */}
        <IconButton
          aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          onClick={toggleTheme}
          size="small"
          sx={{
            width: 32,
            height: 32,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            borderRadius: 1,
            color: 'text.secondary',
            flexShrink: 0,
            '&.Mui-focusVisible': { outline: 'none', boxShadow: focusRing },
          }}
        >
          {isDarkMode ? <LightMode sx={{ fontSize: 15 }} /> : <DarkMode sx={{ fontSize: 15 }} />}
        </IconButton>

        {/* Notifications bell — enabled for every authenticated user, no
            permission key (AC-27). */}
        <NotificationBell />

        {/* User button */}
        <ButtonBase
          aria-label="Account menu"
          onClick={onUserMenuOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            px: '6px',
            py: '4px',
            borderRadius: 1,
            flexShrink: 0,
            '&:hover': { bgcolor: theme.palette.accent.tint },
            '&.Mui-focusVisible': { outline: 'none', boxShadow: focusRing },
          }}
        >
          <PersonAvatar name={fullName} src={user?.avatarUrl} size={28} fontSize={11} />
          {/* Name/role/chevron hide on small screens so the bar never overflows —
              the avatar alone still opens the account menu. */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.15, minWidth: 0 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>
              {fullName || user?.email}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.disabled', whiteSpace: 'nowrap' }}>
              {user?.role}
            </Typography>
          </Box>
          <KeyboardArrowDown sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 12, color: 'text.disabled', ml: '2px' }} />
        </ButtonBase>

        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={onUserMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{ paper: { sx: { mt: 0.5 } } }}
        >
          {/* Personal preferences live beside the avatar, not in the left nav:
              organisation-wide configuration lives in Admin and in Projects →
              Settings, and these settings are the signed-in user's own. */}
          <MenuItem
            onClick={() => {
              onUserMenuClose();
              navigate('/settings');
            }}
          >
            <ListItemIcon>
              <TuneIcon fontSize="small" />
            </ListItemIcon>
            Settings
          </MenuItem>
          <MenuItem
            onClick={() => {
              onUserMenuClose();
              setProfileDialogOpen(true);
            }}
          >
            <ListItemIcon>
              <PhotoCameraIcon fontSize="small" />
            </ListItemIcon>
            Profile picture
          </MenuItem>
          {/* Only an account holding a password has one here to change; an SSO user's
              lives with their identity provider. Absent rather than disabled —
              a greyed entry would pose a question the menu cannot answer. */}
          {userHasPassword(user) && (
            <MenuItem
              onClick={() => {
                onUserMenuClose();
                navigate(`/settings#${SECURITY_SECTION_ID}`);
              }}
            >
              <ListItemIcon>
                <LockIcon fontSize="small" />
              </ListItemIcon>
              Change password
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              onUserMenuClose();
              navigate('/about');
            }}
          >
            <ListItemIcon>
              <AboutIcon fontSize="small" />
            </ListItemIcon>
            {ABOUT_COPY.menuItem}
          </MenuItem>
          <MenuItem onClick={onLogout}>
            <ListItemIcon>
              <ExitToApp fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>

        <ProfileAvatarDialog
          open={profileDialogOpen}
          onClose={() => setProfileDialogOpen(false)}
        />
      </Toolbar>
    </AppBar>
  );
}
